import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";

/**
 * Polls the internal chat unread count every 30s while the tab is visible.
 * Returns `{ total, perPatient }`. Never throws — network failures leave the
 * last successful count in place.
 *
 * Consumers:
 *   - Patient sidebar → badge next to the "Chat" nav item (total)
 *   - Admin shell / patient row → badge next to admin chat + per-patient dots
 */
export function useUnreadMessages(enabled = true) {
  const [state, setState] = useState({ total: 0, perPatient: {} });
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return () => {};
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/messages/unread-count");
        if (alive) setState({ total: data.total || 0, perPatient: data.per_patient || {} });
      } catch (_) { /* silent — keep previous count */ }
    };
    load();
    const schedule = () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (document.visibilityState === "visible") {
        timerRef.current = setInterval(load, 30000);
      }
    };
    schedule();
    const onVis = () => { if (document.visibilityState === "visible") load(); schedule(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVis);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [enabled]);

  return state;
}
